import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import LaptopIcon from "@mui/icons-material/Laptop";
import PlaceIcon from "@mui/icons-material/Place";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { hasSession, isReauthCancelled } from "../../authClient";
import {
  approveCode, codeFromLocation, denyCode, explainCodeError, formatCode, lookupCode, rememberLinkPage, takeAfterLoginPath
} from "../../auth/deviceLogin";
import { COLORS } from "../Home/constants";
import { ghostButtonSx, inputSx, panelSx, primaryButtonSx } from "../Home/styles";

// ─────────────────────────────────────────────────────────────────────────────
// /link — the phone's side of "Sign in with a code from your phone".
//
// Opened by scanning the computer's QR (studio.fennira.com/link#K7M42Q) or from
// Settings → Security → "Sign in on a computer". The person sees WHAT is asking
// and roughly WHERE before anything happens, and approving needs a passkey
// check right then (the server asks; authClient shows "Confirm it's you").
//
// The warning is the point of this page: the one way to abuse a code flow is to
// talk someone into approving a code that isn't theirs.
// ─────────────────────────────────────────────────────────────────────────────

const ago = (s) => (s < 10 ? "just now" : s < 60 ? `${s} seconds ago` : `${Math.round(s / 60)} min ago`);
const timeOf = (d) => new Date(d).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export default function LinkDevice() {
  const navigate = useNavigate();
  const signedIn = useMemo(() => hasSession(), []);
  const [code, setCode] = useState(() => codeFromLocation());
  const [info, setInfo] = useState(null);        // the lookup answer
  const [step, setStep] = useState("code");      // code → confirm → approved | denied
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Like the other Studio pages: opt out of App.css's old blanket mobile rules
  // (every row a column, every button full width).
  useLayoutEffect(() => {
    document.body.classList.add("fn-studio");
    return () => document.body.classList.remove("fn-studio");
  }, []);

  // Keep the code out of the address bar (and history) once read; remember it
  // in case a sign-in has to happen first.
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", "/link");
    if (code) rememberLinkPage(code);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const look = async (value = code) => {
    setBusy(true); setError("");
    try {
      const data = await lookupCode(value);
      takeAfterLoginPath();   // arrived where it was going
      setInfo(data); setStep("confirm");
    } catch (err) {
      setError(explainCodeError(err));
    } finally { setBusy(false); }
  };

  // Scanned: look it up at once.
  useEffect(() => {
    if (signedIn && code.replace("-", "").length === 6) look(code);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Another QR scanned while this page is already open in the same tab.
  useEffect(() => {
    const onHash = () => {
      const next = codeFromLocation();
      if (!next) return;
      window.history.replaceState(null, "", "/link");
      setCode(next); setInfo(null); setStep("code"); setError("");
      if (signedIn) look(next); else rememberLinkPage(next);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [signedIn]);   // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async () => {
    setBusy(true); setError("");
    try {
      await approveCode(code);
      setStep("approved");
    } catch (err) {
      if (!isReauthCancelled(err)) setError(explainCodeError(err));
    } finally { setBusy(false); }
  };

  const deny = async () => {
    setBusy(true); setError("");
    try { await denyCode(code); setStep("denied"); } catch (err) { setError(explainCodeError(err)); } finally { setBusy(false); }
  };

  let body;
  if (!signedIn) {
    body = (
      <Stack spacing={2}>
        <Typography sx={{ color: COLORS.textSoft, fontSize: 15, lineHeight: 1.6 }}>
          To sign in a computer, this phone must be signed in to Fennira first. After signing in you'll come back here
          {code ? " with the code already filled in" : ""}.
        </Typography>
        <Button variant="contained" onClick={() => { rememberLinkPage(code); navigate("/login"); }} sx={primaryButtonSx}>
          Sign in on this phone
        </Button>
      </Stack>
    );
  } else if (step === "code") {
    body = (
      <Box component="form" onSubmit={(e) => { e.preventDefault(); look(); }}>
        <Typography sx={{ color: COLORS.textSoft, fontSize: 15, lineHeight: 1.6, mb: 2 }}>
          Enter the code shown on the computer.
        </Typography>
        <TextField autoFocus fullWidth value={code} onChange={(e) => setCode(formatCode(e.target.value))}
          placeholder="K7M-42Q" disabled={busy} autoComplete="one-time-code"
          inputProps={{ "aria-label": "Code from the computer", inputMode: "text", autoCapitalize: "characters", spellCheck: false,
            style: { fontFamily: COLORS.fontMono, fontSize: 26, letterSpacing: "0.14em", textAlign: "center" } }}
          sx={inputSx} />
        <Button type="submit" fullWidth variant="contained" disabled={busy || code.replace("-", "").length !== 6}
          sx={{ ...primaryButtonSx, mt: 2, height: 46 }}>
          {busy ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Continue"}
        </Button>
      </Box>
    );
  } else if (step === "confirm" && info) {
    const reauth = info.kind === "reauth";
    const blocked = !!info.restrictedUntil;
    const row = (icon, text) => (
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box sx={{ color: COLORS.muted, display: "flex" }}>{icon}</Box>
        <Typography sx={{ color: COLORS.text, fontSize: 15 }}>{text}</Typography>
      </Stack>
    );
    body = (
      <Stack spacing={2.25}>
        <Box sx={{ p: 2, borderRadius: "14px", border: `1px solid ${COLORS.borderStrong}`, bgcolor: COLORS.panelSoft }}>
          <Stack spacing={1.1}>
            {row(<LaptopIcon fontSize="small" />, <strong>{info.device}</strong>)}
            {row(<PlaceIcon fontSize="small" />, info.place ? `${info.place} (approximate)` : "Unknown location")}
            {row(<ScheduleIcon fontSize="small" />, `Asked ${ago(info.askedSecondsAgo)}`)}
          </Stack>
        </Box>
        {info.otherCountry && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ borderRadius: "12px" }}>
            This computer seems to be in another country than this phone. If you aren't signing in yourself right now,
            press Deny.
          </Alert>
        )}
        <Typography sx={{ color: COLORS.textSoft, fontSize: 14, lineHeight: 1.6 }}>
          <strong>Only approve if YOU are {reauth ? "using" : "signing in on"} a computer in front of you right now.</strong>{" "}
          Fennira will never send you a code to approve. {reauth
            ? "Approving lets that computer open stream keys and reporter setup for the next 12 hours, and security settings for 5 minutes."
            : "Approving signs that computer in to your account."}
        </Typography>
        {blocked && (
          <Alert severity="info" sx={{ borderRadius: "12px" }}>
            This phone was signed in with a recovery code, so it can approve computers from {timeOf(info.restrictedUntil)}.
          </Alert>
        )}
        <Stack direction="row" spacing={1.25}>
          <Button fullWidth onClick={deny} disabled={busy} sx={{ ...ghostButtonSx, height: 46 }}>Deny</Button>
          <Button fullWidth variant="contained" onClick={approve} disabled={busy || blocked} sx={{ ...primaryButtonSx, height: 46 }}>
            {busy ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Approve"}
          </Button>
        </Stack>
      </Stack>
    );
  } else if (step === "approved") {
    body = (
      <Stack spacing={1.5} alignItems="center" sx={{ textAlign: "center", py: 2 }}>
        <CheckCircleIcon sx={{ fontSize: 48, color: COLORS.green }} />
        <Typography sx={{ color: COLORS.text, fontSize: 18, fontWeight: 700 }}>Approved</Typography>
        <Typography sx={{ color: COLORS.textSoft, fontSize: 14.5, lineHeight: 1.6 }}>
          {info?.kind === "reauth"
            ? "You can continue on the computer."
            : `${info?.device || "The computer"} is signing in now. You can see and sign it out in Settings → Security.`}
        </Typography>
      </Stack>
    );
  } else {
    body = (
      <Stack spacing={1.5} alignItems="center" sx={{ textAlign: "center", py: 2 }}>
        <BlockIcon sx={{ fontSize: 44, color: COLORS.red }} />
        <Typography sx={{ color: COLORS.text, fontSize: 18, fontWeight: 700 }}>Denied</Typography>
        <Typography sx={{ color: COLORS.textSoft, fontSize: 14.5, lineHeight: 1.6 }}>
          Nothing was signed in. If you didn't start this, someone may have sent you their code: don't approve codes you
          receive from anyone.
        </Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.bg, color: COLORS.text, fontFamily: COLORS.fontSans, px: 2, py: { xs: 2, md: 5 }, textAlign: "left" }}>
      <Box sx={{ maxWidth: 460, mx: "auto" }}>
        {signedIn && (
          <Button component={RouterLink} to="/home" startIcon={<ArrowBackIcon />}
            sx={{ color: COLORS.muted, textTransform: "none", fontWeight: 600, mb: 1.5, px: 1 }}>
            Back to the studio
          </Button>
        )}
        <Box sx={panelSx}>
          <Typography component="h1" sx={{ fontFamily: COLORS.fontDisplay, fontWeight: 800, fontSize: 26, lineHeight: 1.2, mb: 2 }}>
            {step === "confirm" && info?.kind === "reauth" ? "Confirm on a computer?" : "Sign in on a computer?"}
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}
          {body}
          {step === "confirm" && (
            <Button onClick={() => { setStep("code"); setInfo(null); setCode(""); setError(""); }} disabled={busy}
              sx={{ mt: 1.5, color: COLORS.muted, textTransform: "none" }}>
              Use another code
            </Button>
          )}
          {(step === "approved" || step === "denied") && (
            <Button onClick={() => { setStep("code"); setInfo(null); setCode(""); }} sx={{ ...ghostButtonSx, mt: 2, width: "100%" }}>
              Enter another code
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

